/**
 * Global Shell Store — Zustand
 *
 * Provides:
 *  - Auth state (current user, token, refresh token)
 *  - UI state (active app)
 *  - Cross-app event bus (emit / subscribe pattern)
 *  - Automatic token refresh
 */

import { create } from "zustand";

import {
  isWorkspacePausedBody,
  purgeLegacyRefreshToken,
  refreshWithRetry,
  STORAGE_ACCESS_TOKEN,
  STORAGE_USER,
  WECO_USER_UPDATED_EVENT,
} from "@weco/suite-client";

import { peopleAuthUrl } from "../config/peopleApi";
import { setLastLoginTimestamp } from "../hooks/useLastLogin";
import {
  clearWorkspacePausedNotice,
  markWorkspacePaused,
} from "../lib/billing/workspacePausedNotice";

// ---- Types ----

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  roles: string[];
  tenantId: string;
  isSuperAdmin?: boolean;
  /** System-level scopes (`admin_scopes`), from the user's system roles. */
  adminPermissions?: string[];
  /**
   * Org-scoped admin capabilities, flattened from the token's `org_scopes`.
   *
   * Module scopes (`email:access`, …) are excluded on purpose: the default
   * Member role grants every one of them to every user as a fail-open
   * rollout guarantee, so "has any org scope" is true for everybody and
   * would be useless as a signal. What is left is the real administrative
   * capability — `users:manage`, `roles:manage`, `audit:read` and so on.
   */
  orgAdminPermissions?: string[];
}

export type EventName = string; // e.g. "chat:new-message", "calendar:event-created"

type EventCallback = (payload: unknown) => void;

interface ShellStore {
  // Auth
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  setUser: (user: User, accessToken: string, refreshToken?: string) => void;
  /** Merge fields into the current user and persist (e.g. after profile PATCH). */
  updateUser: (partial: Partial<User>) => void;
  /** Alias for `updateUser` (profile / avatar propagation to chrome). */
  updateUserProfile: (partial: Partial<User>) => void;
  setTokens: (access: string, refresh: string) => void;
  clearUser: () => void;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<boolean>;
  getValidAccessToken: () => Promise<string | null>;

  // UI
  activeAppId: string | null;
  setActiveApp: (appId: string) => void;

  // Cross-app event bus
  _listeners: Map<EventName, Set<EventCallback>>;
  emit: (event: EventName, payload?: unknown) => void;
  subscribe: (event: EventName, callback: EventCallback) => () => void;
}

// Module-level promise dedup for concurrent refresh calls.
// Mirrors the pattern in packages/suite-client/src/tokenStorage.ts.
let shellRefreshInFlight: Promise<boolean> | null = null;

// Restore persisted auth state
const storedUser = localStorage.getItem(STORAGE_USER);
const storedAccessToken = localStorage.getItem(STORAGE_ACCESS_TOKEN);
const restoredUser = storedUser ? JSON.parse(storedUser) : null;
// The refresh token now lives only in its HttpOnly cookie (ADR-0003's
// follow-up). `refreshToken` below is in-memory for the current page only --
// after a reload it is null and refreshing goes through the cookie, which is
// why nothing may treat a null refreshToken as "signed out".
purgeLegacyRefreshToken();

function parseJwt(token: string): any {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

async function isPausedRefusal(res: Response): Promise<boolean> {
  try {
    return isWorkspacePausedBody(await res.clone().json());
  } catch {
    return false;
  }
}

function isTokenExpiringSoon(token: string | null, bufferSeconds = 120): boolean {
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload || !payload.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - now < bufferSeconds;
}

/**
 * `org_scopes` is `{orgId: [scope, ...]}`. Flatten it to the distinct
 * administrative scopes the user holds anywhere, dropping module access.
 */
export function flattenOrgAdminScopes(
  orgScopes: Record<string, string[]> | undefined | null,
): string[] {
  if (!orgScopes || typeof orgScopes !== "object") return [];
  const out = new Set<string>();
  for (const scopes of Object.values(orgScopes)) {
    if (!Array.isArray(scopes)) continue;
    for (const scope of scopes) {
      if (typeof scope === "string" && !scope.endsWith(":access")) out.add(scope);
    }
  }
  return [...out];
}

/**
 * Re-read the permission claims off an access token.
 *
 * Applied on every token change, not just sign-in: a refresh used to store
 * the new access token and leave the cached user object alone, so a role
 * granted to somebody already signed in never took effect until they signed
 * out and back in. Tokens are short-lived, so re-deriving here means a role
 * change lands at the next refresh instead.
 */
function withTokenPermissions<T extends object>(user: T, accessToken: string): T {
  const payload = parseJwt(accessToken);
  if (!payload) return user;
  return {
    ...user,
    isSuperAdmin: payload.is_super_admin || false,
    adminPermissions: payload.admin_scopes || [],
    orgAdminPermissions: flattenOrgAdminScopes(payload.org_scopes),
  };
}

export const useShellStore = create<ShellStore>((set, get) => ({
  // ---- Auth ----
  user: restoredUser,
  accessToken: storedAccessToken,
  refreshToken: null,
  isAuthenticated: !!restoredUser && !!storedAccessToken,
  isRefreshing: false,

  setUser: (user, accessToken, refreshToken) => {
    const enhancedUser = withTokenPermissions(user, accessToken);
    // A successful sign-in answers "why was I signed out?" — drop the notice.
    clearWorkspacePausedNotice();
    setLastLoginTimestamp(new Date().toISOString());
    localStorage.setItem(STORAGE_USER, JSON.stringify(enhancedUser));
    localStorage.setItem(STORAGE_ACCESS_TOKEN, accessToken);
    // refreshToken is held in memory only; its durable copy is the HttpOnly
    // cookie the login response set.
    set({
      user: enhancedUser,
      accessToken,
      refreshToken:
        refreshToken !== undefined ? refreshToken : get().refreshToken,
      isAuthenticated: true,
    });
  },

  updateUser: (partial) => {
    const { user } = get();
    if (!user) return;
    const updated = { ...user, ...partial };
    localStorage.setItem(STORAGE_USER, JSON.stringify(updated));
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(WECO_USER_UPDATED_EVENT));
    }
    set({ user: updated });
  },

  updateUserProfile: (partial) => get().updateUser(partial),

  setTokens: (access, refresh) => {
    localStorage.setItem(STORAGE_ACCESS_TOKEN, access);
    const current = get().user;
    const user = current ? withTokenPermissions(current, access) : current;
    if (user) localStorage.setItem(STORAGE_USER, JSON.stringify(user));
    set({ accessToken: access, refreshToken: refresh, user });
  },

  clearUser: () => {
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_ACCESS_TOKEN);
    purgeLegacyRefreshToken();
    set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
  },

  logout: async () => {
    const { refreshToken, accessToken, clearUser } = get();

    // Clear local state and storage first so any in-flight requests that 401
    // after this point don't trigger the apiClient's redirect-to-login handler
    // while we're already on our way out.
    clearUser();

    // Fire-and-forget the server-side token revocation. We don't await before
    // navigating because the UX should feel instant; the backend will expire
    // the token regardless via its TTL if the request is lost.
    // Fires whether or not a refresh token is in memory. After a page reload
    // it never is -- the durable copy is the HttpOnly cookie -- and gating on
    // it would mean a reloaded tab signed out locally while its refresh token
    // stayed valid server-side for its full lifetime.
    fetch(peopleAuthUrl("logout/"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
      // `credentials: "include"` sends the refresh cookie for the backend to
      // revoke, and lets its Set-Cookie deletion ride back.
      credentials: "include",
      body: refreshToken ? JSON.stringify({ refresh: refreshToken }) : "{}",
      // keepalive ensures the request completes even as the page unloads
      keepalive: true,
    }).catch(() => {/* server revocation is best-effort */});

    window.location.replace("/login");
  },

  refreshAccessToken: async () => {
    // Concurrent callers await the same in-flight refresh instead of returning
    // false immediately (the previous bug that caused concurrent API calls to
    // all receive null tokens and hard-fail on simultaneous component mounts).
    if (shellRefreshInFlight) {
      return shellRefreshInFlight;
    }

    const { clearUser } = get();
    // No bail on a missing refreshToken. It is in-memory only now, so it is
    // null on every page load, and bailing here would sign out any reloaded
    // tab the moment its access token aged out. The HttpOnly cookie is what
    // actually authenticates this call; the backend answers 401 if it is gone.
    //
    // GH-1913: and the in-memory copy is never SENT. The backend prefers a
    // body token over the cookie, and once any other tab or subdomain has
    // rotated the shared `.we-co.io` cookie this copy is stale -- replaying
    // it past the 10 s reuse grace revokes the whole family and signs every
    // tab out. Cookie-only, same as suite-client's doRefresh.

    shellRefreshInFlight = (async () => {
      set({ isRefreshing: true });
      try {
        // GH-1913: a 429 / 5xx / network error used to land in clearUser()
        // here, which swaps the whole app for LoginScreen (or GuestShell on a
        // call page) and so kicks the user out of their call. Now only a
        // definitive rejection (400/401/403) signs out; anything else is
        // retried and this promise stays pending until it resolves.
        const ok = await refreshWithRetry(
          async () => {
            const res = await fetch(peopleAuthUrl("refresh/"), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: "{}",
            });
            // A member of a workspace paused for an unpaid bill is refused
            // renewal with 403 workspace_paused. That is definitive like any
            // other 403 (auth is cleared below), but LoginScreen must say why
            // rather than look like the session simply crashed.
            if (res.status === 403 && (await isPausedRefusal(res))) markWorkspacePaused();
            return res;
          },
          async (res) => {
            const data = await res.json();
            localStorage.setItem(STORAGE_ACCESS_TOKEN, data.access);
            // Re-read the claims: a role granted since sign-in arrives here.
            const current = get().user;
            const user = current ? withTokenPermissions(current, data.access) : current;
            if (user) localStorage.setItem(STORAGE_USER, JSON.stringify(user));
            set({
              accessToken: data.access,
              refreshToken: data.refresh ?? get().refreshToken,
              isRefreshing: false,
              user,
            });
          },
        );
        if (!ok) clearUser();
        return ok;
      } finally {
        set({ isRefreshing: false });
        shellRefreshInFlight = null;
      }
    })();

    return shellRefreshInFlight;
  },

  getValidAccessToken: async () => {
    const { accessToken, refreshAccessToken } = get();
    
    // Check if token is expiring soon (within 2 minutes)
    if (isTokenExpiringSoon(accessToken, 120)) {
      const success = await refreshAccessToken();
      if (!success) {
        return null;
      }
      return get().accessToken;
    }
    
    return accessToken;
  },

  // ---- UI ----
  activeAppId: null,

  setActiveApp: (appId) => set({ activeAppId: appId }),

  // ---- Event Bus ----
  _listeners: new Map(),

  emit: (event, payload) => {
    const { _listeners } = get();
    const callbacks = _listeners.get(event);
    if (callbacks) {
      callbacks.forEach((cb) => cb(payload));
    }
  },

  subscribe: (event, callback) => {
    const { _listeners } = get();
    if (!_listeners.has(event)) {
      _listeners.set(event, new Set());
    }
    _listeners.get(event)!.add(callback);

    // Return an unsubscribe function
    return () => {
      _listeners.get(event)?.delete(callback);
    };
  },
}));

// Convenience hook for the event bus only
export const useEventBus = {
  emit: (event: EventName, payload?: unknown) =>
    useShellStore.getState().emit(event, payload),
  subscribe: (event: EventName, callback: EventCallback) =>
    useShellStore.getState().subscribe(event, callback),
};
